import React, { useEffect, useRef, useState } from 'react';

// Parallax Background Component
export const ParallaxSection = ({ 
  children, 
  imageUrl, 
  videoUrl,
  speed = 0.5, 
  overlay = true,
  overlayOpacity = 0.4,
  minHeight = '70vh',
  className = ''
}) => {
  const sectionRef = useRef(null);
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrolled = window.scrollY;
        const sectionTop = rect.top + scrolled;
        const relativeScroll = scrolled - sectionTop + window.innerHeight;
        setOffsetY(relativeScroll * speed);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return (
    <section 
      ref={sectionRef}
      className={`relative overflow-hidden ${className}`}
      style={{ minHeight }}
    >
      {/* Parallax Background */}
      <div 
        className="absolute inset-0 w-full h-[120%] -top-[10%]"
        style={{ 
          transform: `translateY(${offsetY}px)`,
          willChange: 'transform'
        }}
      >
        {videoUrl ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
      </div>

      {/* Overlay */}
      {overlay && (
        <div 
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </section>
  );
};

// Parallax Image Component
export const ParallaxImage = ({ 
  src, 
  alt = '', 
  speed = 0.3,
  className = '',
  aspectRatio = '4/3'
}) => {
  const containerRef = useRef(null);
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const elementCenter = rect.top + rect.height / 2;
        const distance = elementCenter - viewportCenter;
        setOffsetY(distance * speed * -1);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return (
    <div 
      ref={containerRef}
      className={`overflow-hidden ${className}`}
      style={{ aspectRatio }}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-[120%] object-cover transition-transform duration-100"
        style={{ 
          transform: `translateY(${offsetY}px) scale(1.1)`,
          willChange: 'transform'
        }}
      />
    </div>
  );
};

// Parallax Text/Element Component - moves element based on scroll
export const ParallaxElement = ({ 
  children, 
  speed = 0.2, 
  direction = 'up',
  className = '' 
}) => {
  const elementRef = useRef(null);
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const elementCenter = rect.top + rect.height / 2;
        const distance = elementCenter - viewportCenter;
        const multiplier = direction === 'up' ? -1 : 1;
        setOffsetY(distance * speed * multiplier);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed, direction]);

  return (
    <div 
      ref={elementRef}
      className={className}
      style={{ 
        transform: `translateY(${offsetY}px)`,
        willChange: 'transform'
      }}
    >
      {children}
    </div>
  );
};

// Fade In on Scroll Component
export const FadeInOnScroll = ({ 
  children, 
  threshold = 0.2,
  delay = 0,
  className = '' 
}) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return (
    <div 
      ref={elementRef}
      className={`transition-all duration-1000 ${className}`}
      style={{ 
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Scale on Scroll Component
export const ScaleOnScroll = ({ 
  children, 
  minScale = 0.95,
  maxScale = 1,
  className = '' 
}) => {
  const elementRef = useRef(null);
  const [scale, setScale] = useState(minScale);

  useEffect(() => {
    const handleScroll = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const elementCenter = rect.top + rect.height / 2;
        const distanceFromCenter = Math.abs(elementCenter - viewportHeight / 2);
        const maxDistance = viewportHeight / 2;
        const progress = 1 - Math.min(distanceFromCenter / maxDistance, 1);
        const newScale = minScale + (maxScale - minScale) * progress;
        setScale(newScale);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [minScale, maxScale]);

  return (
    <div 
      ref={elementRef}
      className={className}
      style={{ 
        transform: `scale(${scale})`,
        willChange: 'transform'
      }}
    >
      {children}
    </div>
  );
};

export default ParallaxSection;
